import React, { useState, useEffect } from 'react';
import {
  UserPlus,
  Clock,
  UserCheck,
  UserX,
  Check,
  X,
  Loader2,
} from 'lucide-react';
import { soundEngine } from '../utils/soundAndSpeech.ts';

export type FriendshipStatus =
  | 'none'
  | 'pending_sent'
  | 'pending_received'
  | 'accepted'
  | 'declined'
  | 'self';

interface FriendButtonProps {
  currentUserUid: string;
  targetUserUid: string;
  initialStatus?: FriendshipStatus;
  size?: 'xs' | 'sm' | 'md';
  onStatusChange?: (newStatus: FriendshipStatus) => void;
  className?: string;
}

export const FriendButton: React.FC<FriendButtonProps> = ({
  currentUserUid,
  targetUserUid,
  initialStatus,
  size = 'sm',
  onStatusChange,
  className = '',
}) => {
  const [status, setStatus] = useState<FriendshipStatus>(
    initialStatus || (currentUserUid === targetUserUid ? 'self' : 'none')
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isHoveredFriends, setIsHoveredFriends] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('qb_auth_token') || '';
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token && !token.startsWith('auth-active-') && !token.startsWith('verified-')) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  };

  // Sync if initialStatus changes
  useEffect(() => {
    if (initialStatus) {
      setStatus(initialStatus);
    }
  }, [initialStatus]);

  if (!currentUserUid || !targetUserUid || currentUserUid === targetUserUid || status === 'self') {
    return null;
  }

  const handleSendRequest = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLoading) return;
    setIsLoading(true);
    soundEngine.playClick();
    try {
      const res = await fetch('/api/friends/request', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          requesterUid: currentUserUid,
          targetUid: targetUserUid,
        }),
      });
      if (res.ok) {
        setStatus('pending_sent');
        onStatusChange?.('pending_sent');
      }
    } catch (err) {
      console.error('Failed to send friend request:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRespond = async (e: React.MouseEvent, action: 'accepted' | 'declined') => {
    e.stopPropagation();
    if (isLoading) return;
    setIsLoading(true);
    soundEngine.playClick();
    try {
      const res = await fetch('/api/friends/respond', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          requesterUid: targetUserUid,
          addresseeUid: currentUserUid,
          action,
        }),
      });
      if (res.ok) {
        setStatus(action);
        onStatusChange?.(action);
      }
    } catch (err) {
      console.error('Failed to respond to friend request:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnfriend = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLoading) return;
    setIsLoading(true);
    soundEngine.playClick();
    try {
      const res = await fetch('/api/friends/unfriend', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          userUid: currentUserUid,
          targetUid: targetUserUid,
        }),
      });
      if (res.ok) {
        setStatus('none');
        onStatusChange?.('none');
      }
    } catch (err) {
      console.error('Failed to unfriend:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const sizeClasses = {
    xs: 'px-2 py-0.5 text-[10px] rounded-lg gap-1',
    sm: 'px-2.5 py-1 text-xs rounded-xl gap-1.5',
    md: 'px-3.5 py-2 text-xs font-semibold rounded-xl gap-2',
  }[size];

  const iconSizes = {
    xs: 'w-3 h-3',
    sm: 'w-3.5 h-3.5',
    md: 'w-4 h-4',
  }[size];

  if (isLoading) {
    return (
      <div
        className={`inline-flex items-center justify-center border border-[#404040] bg-[#1C1C1C] text-[#A3A3A3] ${sizeClasses} ${className}`}
      >
        <Loader2 className={`${iconSizes} animate-spin`} />
        <span>Updating...</span>
      </div>
    );
  }

  if (status === 'accepted') {
    return (
      <button
        type="button"
        onClick={handleUnfriend}
        onMouseEnter={() => setIsHoveredFriends(true)}
        onMouseLeave={() => setIsHoveredFriends(false)}
        title="Click to Unfriend"
        className={`inline-flex items-center font-medium border transition-colors cursor-pointer ${
          isHoveredFriends
            ? 'border-red-500/50 bg-red-950/30 text-red-400 hover:bg-red-950/50'
            : 'border-emerald-500/40 bg-emerald-950/20 text-emerald-400'
        } ${sizeClasses} ${className}`}
      >
        {isHoveredFriends ? (
          <>
            <UserX className={iconSizes} />
            <span>Unfriend</span>
          </>
        ) : (
          <>
            <UserCheck className={iconSizes} />
            <span>Friends</span>
          </>
        )}
      </button>
    );
  }

  if (status === 'pending_sent') {
    return (
      <div
        className={`inline-flex items-center font-medium border border-[#404040] bg-[#1C1C1C] text-amber-400 select-none ${sizeClasses} ${className}`}
      >
        <Clock className={iconSizes} />
        <span>Request Sent</span>
      </div>
    );
  }

  if (status === 'pending_received') {
    return (
      <div className={`inline-flex items-center gap-1.5 ${className}`}>
        <button
          type="button"
          onClick={(e) => handleRespond(e, 'accepted')}
          className={`inline-flex items-center font-semibold bg-emerald-600 hover:bg-emerald-500 text-white shadow-xs transition-colors cursor-pointer ${sizeClasses}`}
        >
          <Check className={iconSizes} />
          <span>Accept</span>
        </button>
        <button
          type="button"
          onClick={(e) => handleRespond(e, 'declined')}
          className={`inline-flex items-center font-medium border border-[#404040] bg-[#1C1C1C] hover:bg-[#262626] text-[#A3A3A3] transition-colors cursor-pointer ${sizeClasses}`}
        >
          <X className={iconSizes} />
          <span>Decline</span>
        </button>
      </div>
    );
  }

  // Default: 'none' or 'declined' -> can send friend request
  return (
    <button
      type="button"
      onClick={handleSendRequest}
      className={`inline-flex items-center font-semibold bg-[#262626] hover:bg-[#333333] border border-[#404040] text-[#FAFAFA] hover:text-white transition-all cursor-pointer shadow-xs ${sizeClasses} ${className}`}
    >
      <UserPlus className={`${iconSizes} text-[#F97316]`} />
      <span>Add Friend</span>
    </button>
  );
};
